import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
} from '@angular/core';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { ResponseError, SalesApi } from '../../connectors/backend';
import { AuthService } from '../../services/auth-service';
import { CompanyService } from '../../services/company.service';
import { salesQueryKeys } from '../../services/query-keys';
import { apiOnline } from '../../utils/api-connectivity';
import { parseApiErrorDetail } from '../../utils/api-error';
import { LANGUAGE } from '../../utils/internationalization';
import { STORAGE_KEYS } from '../../utils/storage-keys';
import { readAgentBoot, saveAgentBoot } from './agent-boot';

/** A 4xx answers the same on a retry, and with the API out of reach the reads go to the device. */
export const retryUnlessClientError = (failureCount: number, error: Error) =>
  !(
    error instanceof ResponseError &&
    error.response.status >= 400 &&
    error.response.status < 500
  ) &&
  failureCount < 3 &&
  apiOnline();

export function myRepQueryOptions(sales: SalesApi, companyId: string) {
  return {
    queryKey: salesQueryKeys.myRep(companyId),
    queryFn: () => sales.getMySalesRep({ xCompanyId: companyId }),
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessClientError,
    // The guard has already retried; the layout shows the failure at once.
    retryOnMount: false,
    enabled: !!companyId,
  };
}

/** The context every page of the agents app reads: company, user, rep link and channel config. */
@Injectable({ providedIn: 'root' })
export class AgentSession {
  private readonly sales = inject(SalesApi);
  private readonly companies = inject(CompanyService);
  private readonly auth = inject(AuthService);
  private readonly queryClient = inject(QueryClient);

  /** Set inside the app: its guard waits for it. */
  readonly companyId = computed(() => this.companies.companyId() ?? '');
  readonly me = this.auth.me;
  readonly userId = computed(() => this.me()?.id ?? '');
  /** What the last online start kept: with no connection the queries have no answer. */
  private readonly boot = computed(() => {
    const boot = readAgentBoot();
    return boot?.me.id === this.userId()
      ? boot.companies[this.companyId()]
      : undefined;
  });
  readonly culture = computed(
    () =>
      (this.companies.companies().length
        ? undefined
        : (this.boot()?.culture as LANGUAGE | undefined)) ??
      this.companies.contentCultureFor(this.companyId()),
  );

  readonly repQuery = injectQuery(() =>
    myRepQueryOptions(this.sales, this.companyId()),
  );
  readonly rep = computed(() => this.repQuery.data() ?? this.boot()?.rep);

  readonly configQuery = injectQuery(() => {
    const xCompanyId = this.companyId();
    return {
      queryKey: salesQueryKeys.config(xCompanyId),
      queryFn: () => this.sales.getSalesConfig({ xCompanyId }),
      staleTime: 60 * 60 * 1000,
      retry: retryUnlessClientError,
      enabled: !!xCompanyId,
    };
  });
  readonly config = computed(
    () => this.configQuery.data() ?? this.boot()?.config,
  );
  readonly currency = computed(() => this.config()?.currencyCode);
  readonly priceDecimals = computed(() => this.config()?.priceDecimals);
  /** The phases in display order, each flagged sold and in progress. */
  readonly orderPhases = computed(() => this.config()?.orderPhases ?? []);
  /** The offer board's columns after the drafts, in display order. */
  readonly offerColumns = computed(() => this.config()?.offerColumns ?? []);
  /** The column of the offers awaiting the customer's answer, when the settings hold one. */
  readonly awaitingOfferColumn = computed(() =>
    this.offerColumns().find((column) => column.answer === 'none'),
  );

  constructor() {
    effect(() => {
      const me = this.me();
      const rep = this.repQuery.data();
      const config = this.configQuery.data();
      if (me && rep && config && this.companies.companies().length) {
        const companyId = this.companyId();
        const culture = this.companies.contentCultureFor(companyId);
        saveAgentBoot(me, companyId, { rep, config, culture });
      }
    });
  }

  /**
   * A read refused for a phase or an offer column the settings no longer hold
   * reads the settings again, so the columns and the reads keyed by them follow.
   */
  refreshPhasesOn(error: unknown): void {
    if (!(error instanceof ResponseError) || error.response.status !== 400) {
      return;
    }
    const companyId = this.companyId();
    void parseApiErrorDetail(error).then((detail) => {
      if (detail?.errors?.['phase'] || detail?.errors?.['offerColumn']) {
        void this.queryClient.invalidateQueries({
          queryKey: salesQueryKeys.config(companyId),
        });
      }
    });
  }

  /** The API's rule: an order or a draft is an offer when its type is one of the website's offer types. */
  isOffer(orderType: string | null | undefined): boolean {
    const type = orderType?.trim();
    return (
      !!type &&
      (this.config()?.offerOrderTypes ?? []).some(
        (offerType) => offerType.code.trim() === type,
      )
    );
  }

  /** The types an order draft takes: the agent types that are not offer types. */
  readonly firmOrderTypes = computed(() =>
    (this.config()?.agentOrderTypes ?? []).filter(
      (type) => !this.isOffer(type.code),
    ),
  );
  /**
   * The firm type an order takes without asking: the website's only one, or
   * its fixed agent type; null when the rep picks among several.
   */
  readonly firmOrderTypeWithoutAsking = computed<{ code?: string } | null>(
    () => {
      const types = this.firmOrderTypes();
      if (types.length > 1) return null;
      return {
        code: types[0]?.code ?? (this.config()?.agentOrderType || undefined),
      };
    },
  );

  /** An order type's label among the website's types, else its code. */
  orderTypeLabel(orderType: string | null | undefined): string {
    const type = orderType?.trim();
    if (!type) return '';
    const config = this.config();
    const types = [
      ...(config?.agentOrderTypes ?? []),
      ...(config?.offerOrderTypes ?? []),
      ...(config?.customerOrderTypes ?? []),
    ];
    return types.find((item) => item.code.trim() === type)?.label || type;
  }

  private readonly showReservedKey = computed(
    () => `${STORAGE_KEYS.agentShowReserved}:${this.userId()}`,
  );
  private readonly _showReserved = linkedSignal(() => {
    try {
      return localStorage.getItem(this.showReservedKey()) === 'true';
    } catch {
      return false;
    }
  });
  /** The rep's choice on this device; the catalogue sends it as `includeReserved`. */
  readonly showReserved = this._showReserved.asReadonly();

  setShowReserved(showReserved: boolean): void {
    this._showReserved.set(showReserved);
    try {
      localStorage.setItem(this.showReservedKey(), String(showReserved));
    } catch {
      // storage unavailable: keep the in-memory value only
    }
  }
}
